import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import Footer from "./components/footer";
import Logo from "./components/logo";
import GooglePayButton from "./components/GooglePayButton";
import ApplePayButton from "./components/ApplePayButton";
import TurnstileWidget from "./components/TurnstileWidget";
import AutoPayBanner from "./components/AutoPayBanner";
import { FiAlertCircle } from "react-icons/fi";
import {
  getPaymentTransaction,
  processOneTimePayment,
  recordGooglePayOneTimeTransaction,
  recordApplePayOneTimeTransaction,
} from "./utilities/axios";

const OneTimePayment = () => {
  const [cardNumber, setCardNumber] = useState("");
  const [cardName, setCardName] = useState("");
  const [expiration, setExpiration] = useState("");
  const [cvv, setCvv] = useState("");
  const [saveCard, setSaveCard] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetCounter, setTurnstileResetCounter] = useState(0);

  // Payment details from API
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [error, setError] = useState(null);

  const urlParams = new URLSearchParams(window.location.search);
  const paymentGuid = urlParams.get("paymentGuid");
  const env = urlParams.get("env") || "prod";
  const turnstileSiteKey =
    process.env.REACT_APP_TURNSTILE_SITE_KEY || "0x4AAAAAACl1bd7ovOa17ryI";

  function getMainSiteBaseUrl(env) {
    switch (env) {
      case "local":
        return "http://localhost:8000";
      case "test":
        return "https://test.phoneboxmobile.com/checkout/payment/payment-success";
      default:
        return "https://phoneboxmobile.com/checkout/payment/payment-success";
    }
  }

  const handleCancel = () => {
    // Redirect to main site or client portal
    window.location.href = getMainSiteBaseUrl(env);
  };

  useEffect(() => {
    if (!paymentGuid) {
      setError("Invalid payment link. Please contact support.");
      setIsLoading(false);
      return;
    }

    getPaymentTransaction(paymentGuid)
      .then((response) => {
        console.log("Payment Transaction Response:", response);
        const apiData = response.data.Data || response.data;
        const payment = apiData.content || apiData;

        if ((response.data.Success || apiData.isSuccess) && payment) {
          setPaymentDetails({
            clientId: payment.clientId,
            firstName: payment.firstName,
            lastName: payment.lastName,
            phoneNumber: payment.phoneNumber,
            planName: payment.planName,
            planFee: payment.planFee,
            taxAmount: payment.taxAmount,
            outstandingBalance: payment.outstandingBalance || 0,
            totalAmount: payment.amount, // Using 'amount' from existing API
            newPlanStartDate: payment.newPlanStartDate,
            newPlanEndDate: payment.newPlanEndDate,
            isPaymentCompleted: payment.isPaymentCompleted,
          });
          setIsLoading(false);

          // Check if payment already completed
          if (payment.isPaymentCompleted) {
            setError("This payment has already been completed.");
          }
        } else {
          setError("Failed to fetch payment details. Please contact support.");
          setIsLoading(false);
        }
      })
      .catch((err) => {
        console.error("Error fetching payment info:", err);
        setError(
          "Failed to load payment details. Please try again or contact support.",
        );
        setIsLoading(false);
      });
  }, [paymentGuid]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Check if payment already completed
    if (paymentDetails?.isPaymentCompleted) {
      setError("This payment has already been completed.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    if (!paymentGuid) {
      setError("Invalid payment link.");
      setIsSubmitting(false);
      return;
    }
    if (!turnstileToken) {
      setError("Please complete the security verification.");
      setIsSubmitting(false);
      return;
    }

    const cardData = {
      CreditCardNumber: cardNumber,
      CreditCardName: cardName,
      CCV: cvv,
      ExpirationMonth: expiration.split("/")[0].padStart(2, "0"),
      ExpirationYear: `20${expiration.split("/")[1]}`,
      SaveCard: saveCard,
      TurnstileToken: turnstileToken,
    };
    let shouldResetTurnstile = true;

    console.log("Sending ProcessOneTimePayment request with data:", {
      paymentGuid,
      cardNumber:
        cardNumber.substring(0, 4) +
        "****" +
        cardNumber.substring(cardNumber.length - 4),
      cardName: cardData.CreditCardName,
      expirationMonth: cardData.ExpirationMonth,
      expirationYear: cardData.ExpirationYear.slice(-2),
      saveCard: cardData.SaveCard,
    });

    try {
      const processPaymentResponse = await processOneTimePayment(
        paymentGuid,
        cardData,
      );

      console.log(
        "ProcessOneTimePayment Full Response:",
        processPaymentResponse,
      );
      console.log(
        "ProcessOneTimePayment Response Data:",
        processPaymentResponse.data,
      );

      const paymentResult =
        processPaymentResponse.data?.Data?.content ||
        processPaymentResponse.data?.content ||
        processPaymentResponse.data;

      if (paymentResult.isSuccess) {
        shouldResetTurnstile = false;
        alert(
          "Payment successful! Your plan change request has been processed. Redirecting...",
        );
        console.log(
          "Payment successful! Redirecting to:",
          getMainSiteBaseUrl(env),
        );
        // window.location.href =
        //   getMainSiteBaseUrl(env) + "/us-payment-thank-you";
        window.location.href = "/payment-success";
      } else {
        setError(`Payment failed: ${paymentResult.message}`);
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Error in payment process:", error);

      if (error.response) {
        const errorData = error.response.data;

        const displayError =
          errorData.errorMessage ||
          errorData.message ||
          errorData.Message ||
          "Payment processing failed.";

        alert(displayError);
        setError(displayError);
      } else if (error.request) {
        alert("Network error. Please check your connection.");
        setError("Network error. Please check your connection.");
      } else {
        alert(error.message);
        setError(error.message);
      }
    } finally {
      if (shouldResetTurnstile) {
        setTurnstileResetCounter((current) => current + 1);
      }
      setIsSubmitting(false);
    }
  };

  const handleGooglePaySuccess = async (monerisResponse) => {
    try {
      setIsSubmitting(true);
      setError("");

      const response = await recordGooglePayOneTimeTransaction(
        paymentGuid,
        monerisResponse,
      );

      const result =
        response.data?.content || response.data?.Content || response.data;

      if (result.isSuccess) {
        alert(
          "Google Pay payment successful! Your plan change request has been processed. Redirecting...",
        );
        // window.location.href =
        //   getMainSiteBaseUrl(env) + "/us-payment-thank-you";
        window.location.href = "/payment-success";
      } else {
        setError(`Failed to complete payment: ${result.message}`);
        setIsSubmitting(false);
      }
    } catch (error) {
      if (error.response) {
        const errorData = error.response.data;
        const displayError =
          errorData.errorMessage ||
          errorData.message ||
          errorData.Message ||
          "Payment was approved but failed to record. Please contact support.";
        setError(displayError);
      } else if (error.request) {
        setError(
          "Network error. Payment may have been processed. Please contact support.",
        );
      } else {
        setError(error.message);
      }
      setIsSubmitting(false);
    }
  };

  const handleGooglePayError = (error) => {
    console.error("Google Pay error:", error);
    setError(error.message || "Google Pay payment failed. Please try again.");
    setIsSubmitting(false);
  };

  const handleApplePaySuccess = async (monerisResponse) => {
    try {
      setIsSubmitting(true);
      setError("");

      const response = await recordApplePayOneTimeTransaction(
        paymentGuid,
        monerisResponse,
      );

      const result =
        response.data?.content || response.data?.Content || response.data;

      if (result.isSuccess) {
        alert(
          "Apple Pay payment successful! Your plan change request has been processed. Redirecting...",
        );
        // window.location.href =
        //   getMainSiteBaseUrl(env) + "/us-payment-thank-you";
        window.location.href = "/payment-success";
      } else {
        setError(`Failed to complete payment: ${result.message}`);
        setIsSubmitting(false);
      }
    } catch (error) {
      if (error.response) {
        const errorData = error.response.data;
        const displayError =
          errorData.errorMessage ||
          errorData.message ||
          errorData.Message ||
          "Payment was approved but failed to record. Please contact support.";
        setError(displayError);
      } else if (error.request) {
        setError(
          "Network error. Payment may have been processed. Please contact support.",
        );
      } else {
        setError(error.message);
      }
      setIsSubmitting(false);
    }
  };

  const handleApplePayError = (error) => {
    console.error("Apple Pay error:", error);
    setError(error.message || "Apple Pay payment failed. Please try again.");
    setIsSubmitting(false);
  };

  const handleExpirationInput = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 2) {
      value = value.slice(0, 2) + "/" + value.slice(2, 4);
    }
    setExpiration(value);
  };

  if (isLoading) {
    return (
      <Container>
        <AmountLoading>
          <LoadingSpinner />
        </AmountLoading>
      </Container>
    );
  }

  if (error && !paymentDetails) {
    return (
      <Container>
        <ErrorContainer>
          <h2>Error</h2>
          <p>{error}</p>
          <CancelButton onClick={handleCancel} type="button">
            Return to Dashboard
          </CancelButton>
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <Wrapper>
      <Container>
        <RowContainer>
          <Logo />
          <AmountSection>
            <h3>One-Time Payment</h3>
            <CustomerInfo>
              <InfoLine>
                <strong>
                  {paymentDetails?.firstName} {paymentDetails?.lastName}
                </strong>
              </InfoLine>
              <InfoLine>Customer ID: {paymentDetails?.clientId}</InfoLine>
              <InfoLine>{paymentDetails?.phoneNumber}</InfoLine>
            </CustomerInfo>

            <h3>Your total amount is:</h3>
            <h1>
              <CurrencySymbol>USD </CurrencySymbol>
              <Amount> ${paymentDetails?.totalAmount?.toFixed(2)}</Amount>
            </h1>
          </AmountSection>

          <FormSection>
            <PaymentForm onSubmit={handleSubmit}>
              <AutoPayBanner />
              <Divider>
                <DividerLine />
                <DividerText>Pay with wallet</DividerText>
                <DividerLine />
              </Divider>

              <WalletButtonsRow>
                <WalletItem>
                  <GooglePayButton
                    amount={paymentDetails?.totalAmount}
                    paymentGuid={paymentGuid}
                    onPaymentSuccess={handleGooglePaySuccess}
                    onPaymentError={handleGooglePayError}
                    disabled={
                      isSubmitting ||
                      paymentDetails?.isPaymentCompleted ||
                      !turnstileToken
                    }
                  />
                </WalletItem>

                <WalletItem>
                  <ApplePayButton
                    amount={paymentDetails?.totalAmount}
                    paymentGuid={paymentGuid}
                    onPaymentSuccess={handleApplePaySuccess}
                    onPaymentError={handleApplePayError}
                    disabled={
                      isSubmitting ||
                      paymentDetails?.isPaymentCompleted ||
                      !turnstileToken
                    }
                    isOneTimePayment={true}
                  />
                </WalletItem>
              </WalletButtonsRow>

              <Divider>
                <DividerLine />
                <DividerText>Pay with card</DividerText>
                <DividerLine />
              </Divider>
              <CardFieldsGrid>
                <FormGroup>
                  <label>Card Number</label>
                  <Input
                    type="text"
                    value={cardNumber}
                    onChange={(e) =>
                      setCardNumber(e.target.value.replace(/\D/g, ""))
                    }
                    placeholder="1234 5678 9012 3456"
                    maxLength="20"
                    required
                    disabled={paymentDetails?.isPaymentCompleted}
                  />
                </FormGroup>

                <FormGroup>
                  <label>Name on Card</label>
                  <Input
                    type="text"
                    value={cardName}
                    onChange={(e) => setCardName(e.target.value)}
                    placeholder="James Smith"
                    required
                    disabled={paymentDetails?.isPaymentCompleted}
                  />
                </FormGroup>

                <FormGroup>
                  <label>Expiration Date</label>
                  <Input
                    type="text"
                    value={expiration}
                    onChange={handleExpirationInput}
                    placeholder="MM/YY"
                    maxLength="5"
                    required
                    disabled={paymentDetails?.isPaymentCompleted}
                  />
                </FormGroup>

                <FormGroup>
                  <label>CVV</label>
                  <Input
                    type="text"
                    value={cvv}
                    onChange={(e) => setCvv(e.target.value)}
                    placeholder="123"
                    maxLength="4"
                    required
                    disabled={paymentDetails?.isPaymentCompleted}
                  />
                </FormGroup>
              </CardFieldsGrid>

              <CheckboxContainer htmlFor="saveCardCheckbox">
                <Checkbox
                  id="saveCardCheckbox"
                  type="checkbox"
                  checked={saveCard}
                  onChange={(e) => setSaveCard(e.target.checked)}
                />
                Save for Auto Payment
              </CheckboxContainer>
              <AutopayInfo>
                Your plan will automatically renew using your saved payment
                method unless AutoPay is turned off.
              </AutopayInfo>

              <FormGroup>
                <SecurityCard>
                  <SecurityHeader>
                    <SecurityTitle>Security Verification</SecurityTitle>
                    {/* <SecurityHint>
                      Complete the verification before proceeding with payment.
                    </SecurityHint> */}
                  </SecurityHeader>
                  <TurnstileContainer>
                    <TurnstileWidget
                      siteKey={turnstileSiteKey}
                      resetCounter={turnstileResetCounter}
                      onTokenChange={setTurnstileToken}
                      onError={setError}
                    />
                  </TurnstileContainer>
                </SecurityCard>
              </FormGroup>

              <ButtonContainer>
                <PayButton
                  type="submit"
                  disabled={
                    isSubmitting ||
                    paymentDetails?.isPaymentCompleted ||
                    !turnstileToken
                  }
                >
                  {isSubmitting ? <Spinner /> : "Confirm and Pay"}
                </PayButton>
              </ButtonContainer>
              {error && (
                <ErrorAlert>
                  <ErrorIconWrap>
                    <FiAlertCircle />
                  </ErrorIconWrap>
                  <ErrorText>{error}</ErrorText>
                </ErrorAlert>
              )}

              <ButtonContainer>
                <CancelButton onClick={handleCancel} type="button">
                  Cancel
                </CancelButton>
              </ButtonContainer>
            </PaymentForm>
          </FormSection>
        </RowContainer>

        <Separator />

        <Footer />
      </Container>
    </Wrapper>
  );
};

// Styled Components
const Wrapper = styled.div`
  background: #f7f9fc;
  min-height: 100vh;
  padding: 20px 0;
`;
const Container = styled.div`
  max-width: 1000px;
  margin: 30px auto;
  padding: 64px 64px 32px 64px;
  border-radius: 24px;
  background: white;
  box-shadow:
    0 -2px 4px 0 rgba(0, 79, 232, 0.1) inset,
    0 0 8px 0 rgba(0, 62, 181, 0.12);
  font-family: "Helvetica", "Arial", sans-serif;

  @media (max-width: 768px) {
    max-width: none;
    margin: 1rem 0 0 0;
    padding: 1rem;
  }
`;
const PayButton = styled.button`
  margin-bottom: 10px;
  width: 100%;
  padding: 16px 40px;
  font-weight: 500;
  background: #004fe8;
  font-size: 16px;
  font-weight: 500;
  border: none;
  color: white;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  border-radius: 12px;
  transition: 0.4s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:hover {
    background-color: #d03759;
  }

  &:disabled {
    background: gray;
  }
`;

const CancelButton = styled(PayButton)`
  background: black;
  color: #004fe8;
  border-radius: 12px;
  margin-top: 10px;
  border: 1px solid rgba(221, 225, 230, 0.48);
  background: #e6eeff;
  box-shadow:
    0 -2px 4px 0 rgba(0, 79, 232, 0.1) inset,
    0 0 8px 0 rgba(0, 62, 181, 0.08);
`;
const RowContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  gap: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Separator = styled.hr`
  margin: 30px 0;
  border: none;
  border-top: 1px solid #ddd;
`;

const AmountSection = styled.div`
  text-align: center;
  margin-left: 50px;

  @media (max-width: 768px) {
    margin-top: 64px;
    margin-left: 0;
    width: 100%;
  }
  h3 {
    font-weight: 600;
    font-size: 18px;
    color: #6b7280;
    margin-bottom: 8px;
    letter-spacing: 0;
    line-height: 1.2;
  }
  h1 {
    color: #000;
    font-size: 32px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.64px;
    margin-top: 4px;
    margin-bottom: 16px;
  }
`;

const CurrencySymbol = styled.span`
  font-size: 16px;
  font-weight: 600;
  margin-right: 6px;
  color: #6b7280;
`;

const Amount = styled.span`
  color: #0057ff;
  font-size: 48px;
  font-weight: 700;
  letter-spacing: -0.96px;
  line-height: 1;
`;

const CustomerInfo = styled.div`
  background: #f7faff;
  border-radius: 12px;
  padding: 12px 14px;
  margin: 16px 0;
  text-align: left;
  border: 1px solid rgba(0, 87, 255, 0.08);
`;
const InfoLine = styled.div`
  color: #6b7280;
  font-size: 13px;
  line-height: 18px;

  & + & {
    margin-top: 4px;
  }

  strong {
    color: #111827;
    font-weight: 600;
    font-size: 15px;
  }
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 60px 20px;

  h2 {
    color: #d32f2f;
    margin-bottom: 20px;
  }

  p {
    color: #666;
    margin-bottom: 30px;
    font-size: 1.1rem;
  }

  ${CancelButton} {
    max-width: 300px;
    margin: 0 auto;
  }
`;
const FormSection = styled.div`
  min-width: 400px;

  @media (max-width: 768px) {
    min-width: 100%;
  }
`;

const PaymentForm = styled.form``;
const CardFieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
  label {
    color: #0f1115;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 20px;
  }
`;

const Input = styled.input`
  padding: 16px 20px;
  font-size: 16px;
  margin-top: 10px;

  border-radius: 8px;
  border: 1px solid rgba(138, 178, 255, 0.24);
  background: #e6eeff;

  &::placeholder {
    color: rgba(0, 48, 140, 0.4);
    font-size: 16px;
    font-weight: 400;
    line-height: 1;
    letter-spacing: -0.192px;
  }
`;

const TurnstileContainer = styled.div`
  margin-top: 10px;
`;

// const Row = styled.div`
//   display: flex;
//   justify-content: space-between;
//   gap: 10px;

//   @media (max-width: 768px) {
//     flex-direction: column;
//   }
// `;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;

  color: #3f444c;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
`;

const Checkbox = styled.input`
  cursor: pointer;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 2px solid #548eff;
  background: #fff;

  appearance: none;
  -webkit-appearance: none;
  display: grid;
  place-content: center;

  &:checked {
    background: #548eff;
  }

  &:checked::after {
    content: "";
    width: 10px;
    height: 6px;
    border-left: 2px solid #fff;
    border-bottom: 2px solid #fff;
    transform: rotate(-45deg);
    margin-top: -4px;
  }

  &:focus-visible {
    outline: 2px solid rgba(84, 142, 255, 0.35);
    outline-offset: 2px;
  }
`;
const AutopayInfo = styled.p`
  font-size: 0.6rem;
  color: #555;
  margin-bottom: 20px;
`;

const SecurityCard = styled.div`
  margin: 20px 0 18px;
  padding: 16px;
  border-radius: 14px;
  border: 1px solid rgba(138, 178, 255, 0.2);
  background: #f7faff;
`;

const SecurityHeader = styled.div`
  margin-bottom: 10px;
`;

const SecurityTitle = styled.div`
  color: #0f1115;
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
`;

// const SecurityHint = styled.div`
//   margin-top: 4px;
//   color: #6b7280;
//   font-size: 13px;
//   line-height: 18px;
// `;
const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  /* border: 1px solid red; */
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: ${spin} 1s linear infinite;
  display: inline-block;
`;
const LoadingSpinner = styled.div`
  border: 4px solid rgba(0, 87, 255, 0.12);
  border-top: 4px solid #0057ff;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  animation: ${spin} 1s linear infinite;
`;
const AmountLoading = styled.div`
  height: 58px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: 10px 0 20px 0;
  gap: 10px;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: #ddd;
`;

const DividerText = styled.span`
  color: #999;
  font-size: 14px;
  font-weight: 500;
`;

const WalletButtonsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const WalletItem = styled.div`
  width: 100%;
  min-width: 0;
`;
const ErrorAlert = styled.div`
  margin: 4px 0 14px;
  padding: 12px 14px;
  border: 1px solid rgba(220, 38, 38, 0.14);
  border-radius: 12px;
  background: #fff5f5;
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const ErrorIconWrap = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
  color: #dc2626;
  font-size: 16px;
`;

const ErrorText = styled.div`
  color: #b91c1c;
  font-size: 14px;
  line-height: 20px;
`;

export default OneTimePayment;
